import React, {
  createContext, useState, useContext, useEffect, useRef, useCallback,
} from 'react';
import api from '../api/axios';

const AuthContext = createContext();
const DEFAULT_TIMEOUT_MINUTES = 30;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionTimeoutMinutes, setSessionTimeoutMinutes] = useState(DEFAULT_TIMEOUT_MINUTES);
  const lastActivityRef = useRef(Date.now());

  // ═════════════════════════════════════════════════════
  // Boot: detect state
  // ═════════════════════════════════════════════════════
  useEffect(() => {
    const token = localStorage.getItem('token');
    const activeSchoolId = localStorage.getItem('active_school_id');
    const isSuper = localStorage.getItem('is_super_admin') === 'true';

    if (!token) {
      setLoading(false);
      return;
    }

    // Super admin on picker
    if (isSuper && !activeSchoolId) {
      const name = localStorage.getItem('super_admin_name') || 'Super Admin';
      const parts = name.trim().split(' ');
      setUser({
        id: -1,
        first_name: parts[0] || 'Super',
        last_name: parts.slice(1).join(' ') || 'Admin',
        role: 'super_admin',
        email: '',
        is_super: true,
      });
      setLoading(false);
      return;
    }

    // School user (or super admin inside a school)
    api
      .get('/auth/me')
      .then((res) => setUser(res.data))
      .catch(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('active_school_id');
        localStorage.removeItem('active_school_name');
      })
      .finally(() => setLoading(false));
  }, []);

  // ═════════════════════════════════════════════════════
  // Session timeout (skip for super_admin)
  // ═════════════════════════════════════════════════════
  useEffect(() => {
    if (!user || user.role === 'super_admin') return;
    api
      .get('/admin-settings/session-timeout')
      .then((r) =>
        setSessionTimeoutMinutes(
          r.data.session_timeout_minutes || DEFAULT_TIMEOUT_MINUTES
        )
      )
      .catch(() => setSessionTimeoutMinutes(DEFAULT_TIMEOUT_MINUTES));
  }, [user]);

  // ═════════════════════════════════════════════════════
  // Logout
  // ═════════════════════════════════════════════════════
  const logout = useCallback(() => {
    if (localStorage.getItem('is_super_admin') !== 'true') {
      api.post('/auth/logout').catch(() => {});
    }
    localStorage.removeItem('token');
    localStorage.removeItem('master_token');
    localStorage.removeItem('is_super_admin');
    localStorage.removeItem('super_admin_name');
    localStorage.removeItem('active_school_id');
    localStorage.removeItem('active_school_name');
    setUser(null);
    window.location.replace('/login');
  }, []);

  // ═════════════════════════════════════════════════════
  // Track activity
  // ═════════════════════════════════════════════════════
  useEffect(() => {
    if (!user) return;
    const updateActivity = () => { lastActivityRef.current = Date.now(); };
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach((e) => window.addEventListener(e, updateActivity, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, updateActivity));
  }, [user]);

  // ═════════════════════════════════════════════════════
  // Auto-logout on idle
  // ═════════════════════════════════════════════════════
  useEffect(() => {
    if (!user || user.role === 'super_admin') return;
    const timeoutMs = sessionTimeoutMinutes * 60 * 1000;
    const check = () => {
      if (Date.now() - lastActivityRef.current >= timeoutMs) logout();
    };
    const id = setInterval(check, 5000);
    return () => clearInterval(id);
  }, [user, sessionTimeoutMinutes, logout]);

  // ═════════════════════════════════════════════════════
  // School user login
  // ═════════════════════════════════════════════════════
  const login = async (email, password) => {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);
    try {
      const res = await api.post('/auth/token', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      localStorage.setItem('token', res.data.access_token);
      const userRes = await api.get('/auth/me');
      setUser(userRes.data);
      lastActivityRef.current = Date.now();
      return userRes.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  // ═════════════════════════════════════════════════════
  // Super admin login
  // ═════════════════════════════════════════════════════
  const loginAsSuperAdmin = async (email, password) => {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);

    const res = await api.post('/super-admin/token', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    const token = res.data.access_token;
    localStorage.setItem('token', token);
    localStorage.setItem('master_token', token);
    localStorage.setItem('is_super_admin', 'true');
    localStorage.setItem(
      'super_admin_name',
      `${res.data.user.first_name} ${res.data.user.last_name}`
    );
    localStorage.removeItem('active_school_id');
    localStorage.removeItem('active_school_name');

    setUser({
      id: res.data.user.id,
      email: res.data.user.email,
      first_name: res.data.user.first_name,
      last_name: res.data.user.last_name,
      role: 'super_admin',
      is_super: true,
    });
    lastActivityRef.current = Date.now();
    return res.data;
  };

  // ═════════════════════════════════════════════════════
  // Enter school — swap to school-scoped token
  // ═════════════════════════════════════════════════════
  const enterSchool = async (schoolId) => {
    const masterToken = localStorage.getItem('master_token');
    if (!masterToken) throw new Error('No master token — please log in again');

    const res = await api.post(
      `/super-admin/schools/${schoolId}/enter`,
      {},
      { headers: { Authorization: `Bearer ${masterToken}` } }
    );

    localStorage.setItem('token', res.data.access_token);
    localStorage.setItem('active_school_id', String(res.data.school.id));
    localStorage.setItem('active_school_name', res.data.school.name);

    setUser(res.data.user);
    lastActivityRef.current = Date.now();

    window.location.href = '/dashboard';
    return res.data;
  };

  // ═════════════════════════════════════════════════════
  // Switch back to picker — restore master token
  // ═════════════════════════════════════════════════════
  const switchSchool = useCallback(() => {
    const masterToken = localStorage.getItem('master_token');
    if (masterToken) localStorage.setItem('token', masterToken);
    localStorage.removeItem('active_school_id');
    localStorage.removeItem('active_school_name');
    window.location.href = '/select-school';
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        loginAsSuperAdmin,
        enterSchool,
        switchSchool,
        logout,
        loading,
        sessionTimeoutMinutes,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}